import { FC, useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { groupInspections } from '@/utils/inspection.utils.ts';
import { clivApiRequest } from '@/utils/apiClient';
import { captureApiFailure } from '@/utils/sentryApiError.util';
import { ApiRequestOptions } from '@/types/ApiRequestOptions';
import { LoadingStatus } from '@/enums/LoadingStatus.enum';
import { InspectionsFilter } from '@/features/inspection-list/components/filter/InspectionsFilter';
import { InspectionPaginatedResponse } from '@/types/Inspection.type';
import { InspectionCard } from '@/features/inspection-list/components/InspectionCard';
import { InspectionFilterSkeleton } from '@/features/inspection-list/components/filter/InspectionFilterSkeleton';
import { InspectionCardSkeleton } from '@/features/inspection-list/components/InspectionCardSkeleton';
import { setInspectionList, setSizeCharts } from '@/store/slices/inspectionsSlice';
import { setPageHeader } from '@/store/slices/appDataSlice';
import { RootState } from '@/store/rootReducer';
import { APP_CONFIG_KEY } from '@/types/AppConfig.type';
import { store } from '@/store/store';
import { SyncProgress } from '../components/sync-progress/SyncProgress';
import { useAppSelector } from '@/store/hooks';
import { SyncStatus } from '@/enums/SyncStatus.enum';
import { systemConfig } from '@/config/systemConfig';
import { InspectUploadStatus } from '@/types/InspectUploadStatus';
import { useInspectionFilters } from './hooks/useInspectionFilters';
import {
  useControlPoints,
  useDivisions,
  useClientDecisions,
  useImageCategories,
  useCheckpointChecks,
  useDefectData,
} from '@/hooks/masterData';
import { ALL_CONTROL_POINTS_ID } from '@/types/ControlPoint';

import styles from './InspectionList.module.scss';

export const InspectionPage: FC = () => {
  const uploadStatus = useAppSelector((state: RootState) => state.uploadProgress.status);

  const config = useSelector((state: RootState) => state.appConfig);
  const { t: translate } = useTranslation();
  const inspectionsData = useAppSelector((state: RootState) => state.inspectionsData.inspections);

  // Use useMemo to optimize the expensive grouping operation
  const inspections = useMemo(() => {
    return groupInspections(
      inspectionsData,
      (item) => [item.product?.id, item.inspectionDate, item.supplier?.id || ''],
      (item) => ({
        product: item.product,
        inspectionDate: item.inspectionDate,
        supplier: item.supplier,
        controlPoint: item.controlPoint,
        lotSize: item.lotSize,
        inspectionId: item.id,
        externalPO: item.externalOrderReference,
        factory: item.factory,
      })
    );
  }, [inspectionsData]);

  const masterDataListOptions = { refreshIfEmpty: true };
  const { controlPoints, status: controlPointsStatus } = useControlPoints(masterDataListOptions);
  const { divisions } = useDivisions();
  const { status: clientDecisionsStatus } = useClientDecisions(masterDataListOptions);
  const { status: imageCategoriesStatus } = useImageCategories(masterDataListOptions);
  const { status: checkpointChecksStatus } = useCheckpointChecks(masterDataListOptions);

  // Use custom hook for filter state management
  const {
    productCode,
    orderReference,
    controlPoint,
    startDate,
    endDate,
    status,
    datesSettings,
    setStatus,
    handleProductCodeChange,
    handleOrderReferenceChange,
    handleControlPointChange,
    handleInspectionStartDateChange,
    handleInspectionEndDateChange,
    handleResetFilters,
  } = useInspectionFilters({ config, controlPoints: controlPoints || [] });

  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  const { status: defectStatus } = useDefectData(controlPoint, divisions || []);

  const handleCardToggle = (id: string | null) => {
    setExpandedCard((prev) => (prev === id ? null : id));
  };
  const fetchInspections = async () => {
    if (!controlPoint) {
      return;
    }

    // Abort previous request if it exists
    if (abortController) {
      abortController.abort();
    }

    // Create new abort controller for this request
    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    try {
      const params: {
        from: string;
        to: string;
        controlPointIds?: string;
        context: string;
        limit: number;
        timestampField?: string;
      } = {
        from: startDate,
        to: endDate,
        ...(controlPoint !== ALL_CONTROL_POINTS_ID && { controlPointIds: controlPoint }),
        context: 'inspectApp',
        timestampField: 'inspectionDate',
        limit: 999, // ToDo should load all and add infinite scroll
      };

      const url = `${systemConfig.apiUrl}/${API_ENDPOINTS.INSPECTION.INSPECTIONS}`;
      const apirRequest: ApiRequestOptions = {
        url: url,
        params: params,
        signal: newAbortController.signal,
      };

      setStatus((prev) => ({ ...prev, inspections: LoadingStatus.LOADING }));
      const data = await clivApiRequest<InspectionPaginatedResponse>(apirRequest);

      // Check if request was aborted
      if (newAbortController.signal.aborted) {
        return;
      }

      if (data.ok) {
        const inspectionsList = data.data?.data || [];
        const sizeChartList = data.data?.sizeCharts || [];

        store.dispatch(setInspectionList(inspectionsList));
        store.dispatch(setSizeCharts(sizeChartList)); // ToDo: handle size charts
        // store.dispatch(setTemplates(successfulTemplates));
        setStatus((prev) => ({ ...prev, inspections: LoadingStatus.SUCCEEDED }));
      } else {
        store.dispatch(setInspectionList(data.data?.data || []));
        store.dispatch(setSizeCharts(data.data?.sizeCharts || []));
        captureApiFailure('Error fetching inspections', {
          status: data.status,
          extra: {
            url: url,
            params: params,
            data: data,
          },
        });
        setStatus((prev) => ({ ...prev, inspections: LoadingStatus.FAILED }));
      }
    } catch (error) {
      // Don't log error if request was aborted
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      captureApiFailure('Error fetching inspections', { error });
      setStatus((prev) => ({ ...prev, inspections: LoadingStatus.FAILED }));
      console.error('Error fetching inspections:', error);
    } finally {
      // Clear abort controller if it's the current one
      setAbortController((prev) => (prev === newAbortController ? null : prev));
    }
  };
  useEffect(() => {
    if (uploadStatus !== SyncStatus.IN_PROGRESS) {
      fetchInspections();
    }
  }, [controlPoint, startDate, endDate, uploadStatus]);

  useEffect(() => {
    store.dispatch(setPageHeader('All Inspections'));
  }, []);

  function renderInspections() {
    if (
      status.inspections === LoadingStatus.LOADING ||
      controlPointsStatus === LoadingStatus.LOADING ||
      defectStatus === LoadingStatus.LOADING
    ) {
      const skeletonCount = Object.entries(inspections || {}).length || 5;
      return (
        <div className={styles['scrollable-item']}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <InspectionCardSkeleton key={index} />
          ))}
        </div>
      );
    }
    // if (uploadStatus === InspectionUploadStatus.Failed) {

    // }
    if (Object.keys(inspections || {}).length === 0) {
      return (
        <div className={styles.noInspectionFound}>
          <div> {translate('NoInspectionsFound')}</div>
        </div>
      );
    }
    return (
      <section className={styles['scrollable-item']}>
        {Object.entries(inspections || {})
          .filter(
            ([, inspection]) =>
              !productCode ||
              inspection.product?.name?.toLowerCase().includes(productCode.toLowerCase()) ||
              inspection.product?.code?.toLowerCase().includes(productCode.toLowerCase())
          )
          .filter(([, inspection]) => {
            if (!orderReference) return true;
            const matchesExternal = inspection.externalPO
              ?.toLowerCase()
              .includes(orderReference.toLowerCase());
            const matchesInternal =
              config[APP_CONFIG_KEY.showInternalOrderRef] &&
              inspection.items?.some((item) =>
                item.internalOrderReference?.toLowerCase().includes(orderReference.toLowerCase())
              );
            return matchesExternal || matchesInternal;
          })
          .sort(([, inspectionA], [, inspectionB]) => {
            const { toUpload } = store.getState().uploadProgress;

            // Helper function to check if any item in the inspection has failed
            const hasFailedItems = (inspection: any) => {
              return inspection.items?.some((item: any) =>
                toUpload?.some(
                  (uploadItem) =>
                    uploadItem.id === item.id &&
                    (uploadItem.status === InspectUploadStatus.Failed ||
                      uploadItem.status === InspectUploadStatus.FailedImages)
                )
              );
            };

            // Check if inspections have failed items
            const hasFailedA = hasFailedItems(inspectionA);
            const hasFailedB = hasFailedItems(inspectionB);

            // Failed inspections should come first
            if (hasFailedA && !hasFailedB) return -1;
            if (!hasFailedA && hasFailedB) return 1;

            return 0;
          })
          .map(([key, inspection]) => (
            <InspectionCard
              key={key}
              {...inspection}
              isExpanded={expandedCard === key}
              onToggle={() => handleCardToggle(key)}
            />
          ))}
      </section>
    );
  }

  function renderFilter() {
    return controlPointsStatus === LoadingStatus.LOADING ? (
      <InspectionFilterSkeleton />
    ) : (
      <section className={styles['fixed-item']}>
        {/* <section className={styles.header}>{}</section> */}
        <InspectionsFilter
          {...{
            productCode,
            orderReference,
            controlPoint,
            startDate,
            endDate,
            minDate: datesSettings.min,
            maxDate: datesSettings.max,
          }}
          controlPoints={controlPoints || []}
          onProductCodeChange={handleProductCodeChange}
          onOrderReferenceChange={handleOrderReferenceChange}
          onControlPointChange={handleControlPointChange}
          onInspectionStartDateChange={handleInspectionStartDateChange}
          onInspectionEndDateChange={handleInspectionEndDateChange}
          onResetFilters={handleResetFilters}
        />
      </section>
    );
  }
  const IsServiceWorkerSupported = () => {
    return 'serviceWorker' in navigator;
  };
  if (
    controlPointsStatus === LoadingStatus.FAILED ||
    status.inspections === LoadingStatus.FAILED ||
    defectStatus === LoadingStatus.FAILED ||
    clientDecisionsStatus === LoadingStatus.FAILED ||
    imageCategoriesStatus === LoadingStatus.FAILED ||
    checkpointChecksStatus === LoadingStatus.FAILED
  )
    return <>Failed to load inspections</>;
  return (
    <div className={styles.inspectionsContainer}>
      {!IsServiceWorkerSupported() && (
        <div className={styles.serviceWorkerNotSupported}> {translate('SW_Not_Supported')} </div>
      )}
      <SyncProgress></SyncProgress>
      {renderFilter()}
      {renderInspections()}
    </div>
  );
};
